import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import { readStory } from '../api/endpoints';
import type { ReadingQuestionDetail, ReadingStoryDetail, StoryChallengeUsage } from '../api/types';

import { ErrorNotice } from './ErrorNotice';
import { StatTile } from './StatTile';

type StoryDetailDialogProps = {
  storyId: string | null;
  onClose: () => void;
};

const percent = (value: number | null): string => (value === null ? '—' : `${Math.round(value * 100)} %`);

const dateTime = (value: string | null): string =>
  value === null
    ? 'nikdy'
    : new Date(value).toLocaleString('sk-SK', { dateStyle: 'medium', timeStyle: 'short' });

/**
 * A story, as an editor needs to see it.
 *
 * The point of the reading half of the product is a text a child can understand, and until now the back
 * office could say a text existed without ever showing it. Everything here is read-only: the corpus is
 * written through `POST /reading-stories`, and an editing form is a separate piece of work.
 */
export const StoryDetailDialog = ({ storyId, onClose }: StoryDetailDialogProps) => {
  const { data, error, isLoading } = useQuery<ReadingStoryDetail>({
    queryKey: ['story', storyId],
    queryFn: () => readStory(storyId as string),
    enabled: storyId !== null,
  });

  return (
    <Dialog open={storyId !== null} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
          <Box>
            <Typography variant="h6">{data?.title ?? 'Príbeh'}</Typography>
            {data ? (
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', fontFamily: 'ui-monospace, monospace' }}
              >
                {data.code}
              </Typography>
            ) : null}
          </Box>
          {data ? (
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
              <Chip size="small" label={`úroveň ${data.level}`} variant="outlined" />
              <Chip size="small" label={`${data.minAge}–${data.maxAge} r.`} variant="outlined" />
              <Chip
                size="small"
                label={data.status}
                color={data.status === 'published' ? 'success' : 'default'}
                variant="outlined"
              />
            </Stack>
          ) : null}
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <ErrorNotice error={error} />

        {isLoading ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Stack>
        ) : null}

        {data ? (
          <Stack spacing={3}>
            <Box>
              <Typography variant="overline" color="text.secondary">
                Text, ako ho číta dieťa
              </Typography>
              <Paper variant="outlined" sx={{ p: 2.5, mt: 0.5, bgcolor: 'action.hover' }}>
                <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.75, fontSize: '1.05rem' }}>
                  {data.body}
                </Typography>
              </Paper>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {data.wordCount} slov · {data.characterCount} znakov · upravené {dateTime(data.updatedAt)}
              </Typography>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                Otázky ({data.questions.length})
              </Typography>
              <Alert severity="info" variant="outlined" sx={{ mt: 0.5, mb: 1 }}>
                Toto je <strong>poradie, v akom sú odpovede uložené</strong> — nie to, čo vidí dieťa. Pri
                každom zadaní sa možnosti náhodne premiešajú, takže správna odpoveď nie je vždy „a“. Autorská
                konvencia je uvádzať správnu odpoveď ako prvú.
              </Alert>
              <Stack spacing={1.5} sx={{ mt: 0.5 }}>
                {data.questions.map((question: ReadingQuestionDetail, index: number) => (
                  <Paper key={question.id} variant="outlined" sx={{ p: 2 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                      <Typography sx={{ fontWeight: 560 }}>
                        {index + 1}. {question.prompt}
                      </Typography>
                      <Chip
                        size="small"
                        label={question.subskillName ?? question.subskillCode}
                        variant="outlined"
                        sx={{ flexShrink: 0 }}
                      />
                    </Stack>
                    <Stack spacing={0.5} sx={{ mt: 1.25 }}>
                      {question.options.map((option: string, optionIndex: number) => (
                        <Stack key={option} direction="row" spacing={1} alignItems="center">
                          {/*
                            No position letter. The stored index means nothing outside this table — the
                            options are shuffled per instance — and printing `a` beside every correct
                            answer is what made a working engine look broken.
                          */}
                          <Chip
                            size="small"
                            label={optionIndex === question.correctOptionIndex ? 'správna' : '·'}
                            color={optionIndex === question.correctOptionIndex ? 'success' : 'default'}
                            variant={optionIndex === question.correctOptionIndex ? 'filled' : 'outlined'}
                            sx={{ minWidth: 72 }}
                          />
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: optionIndex === question.correctOptionIndex ? 560 : 400,
                              color:
                                optionIndex === question.correctOptionIndex
                                  ? 'text.primary'
                                  : 'text.secondary',
                            }}
                          >
                            {option}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                    <Divider sx={{ my: 1.25 }} />
                    <Typography variant="body2" color="text.secondary">
                      <strong>Nápoveda:</strong> {question.hint}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                      <strong>Vysvetlenie:</strong> {question.explanation}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary">
                Ako sa príbehu darí
              </Typography>
              {data.usage.timesServed === 0 ? (
                <Alert severity="info" sx={{ mt: 0.5 }}>
                  Tento príbeh ešte nebol ani raz nasadený, takže o ňom nemáme žiadne dáta.
                </Alert>
              ) : (
                <>
                  <Grid container spacing={2} sx={{ mt: 0 }}>
                    <Grid item xs={6} sm={3}>
                      <StatTile
                        label="Nasadení"
                        value={data.usage.timesServed}
                        hint={`naposledy ${dateTime(data.usage.lastServedAt)}`}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatTile
                        label="Úspešnosť"
                        value={percent(data.usage.accuracy)}
                        hint={`${data.usage.correctAttempts} z ${data.usage.attempts} odpovedí`}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatTile
                        label="Pokusy na zadanie"
                        value={data.usage.averageAttempts ?? '—'}
                        hint={`${data.usage.averageHints ?? 0} nápovedy priemerne`}
                      />
                    </Grid>
                    <Grid item xs={6} sm={3}>
                      <StatTile
                        label="Vypršalo"
                        value={data.usage.expired}
                        hint={`${data.usage.solved} vyriešených, ${data.usage.failed} nezvládnutých`}
                      />
                    </Grid>
                  </Grid>

                  <Stack direction="row" spacing={3} sx={{ mt: 2.5 }} flexWrap="wrap" useFlexGap>
                    <Box sx={{ minWidth: 240, flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Podľa úlohy
                      </Typography>
                      <Stack spacing={0.75} sx={{ mt: 0.75 }}>
                        {data.usage.byChallenge.map((row: StoryChallengeUsage) => (
                          <Stack
                            key={row.challengeCode}
                            direction="row"
                            justifyContent="space-between"
                            spacing={2}
                          >
                            <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace' }}>
                              {row.challengeCode}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}
                            >
                              {row.timesServed}× · {percent(row.accuracy)}
                            </Typography>
                          </Stack>
                        ))}
                      </Stack>
                    </Box>

                    <Box sx={{ minWidth: 240, flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        Aké chyby vyvoláva
                      </Typography>
                      {data.usage.errorTypes.length === 0 ? (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                          Žiadna zaznamenaná chyba.
                        </Typography>
                      ) : (
                        <Stack direction="row" spacing={0.75} sx={{ mt: 0.75 }} flexWrap="wrap" useFlexGap>
                          {data.usage.errorTypes.map((row) => (
                            <Chip
                              key={row.errorType}
                              size="small"
                              variant="outlined"
                              label={`${row.errorType} · ${row.occurrences}`}
                            />
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Stack>
                </>
              )}
            </Box>
          </Stack>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};
